import Immutable from 'immutable';
import {
	format,
	parseISO,
	startOfDay,
	endOfDay,
	startOfYear,
	startOfMonth,
	endOfMonth,
	endOfYear,
	addYears as _addYears,
	addMonths as _addMonths
} from 'date-fns';


const dateOrder = 'dmy';
const dateFormat = 'd. M. yyyy';
const timeFormat = 'd. M. yyyy HH:mm:ss';

const yearIndex = dateOrder.indexOf('y');
const monthIndex = dateOrder.indexOf('m');
const dayIndex = dateOrder.indexOf('d');

const currentYearMod100 = new Date().getFullYear() % 100;

const MIN_DATE = new Date(1000, 0, 1);
const MAX_DATE = new Date(3000, 0, 1);

export function pureDateRangesOverlap(from1, to1, from2, to2) {
	from1 = from1 || MIN_DATE;
	from2 = from2 || MIN_DATE;
	to1 = to1 || MAX_DATE;
	to2 = to2 || MAX_DATE;
	return from1.getTime() <= to2.getTime() && from2.getTime() <= to1.getTime();
}

export function truncate(value) {
	if (!value) {
		return value;
	}
	return startOfDay(parseRawDate(value));
}


export function addYears(date, years) {
	return _addYears(parseRawDate(date), years);
}

export function addMonths(date, months) {
	return _addMonths(parseRawDate(date), months);
}

export function getStartOfMonth(value) {
	if (!value) {
		return null;
	}
	return startOfMonth(parseRawDate(value));
}

export function getEndOfMonth(value) {
	if (!value) {
		return null;
	}
	return endOfMonth(parseRawDate(value));
}

export function getStartOfDay(value) {
	if (!value) {
		return null;
	}
	return startOfDay(parseRawDate(value));
}

export function getEndOfDay(value) {
	if (!value) {
		return null;
	}
	return endOfDay(parseRawDate(value));
}

export function getStartOfYear(value) {
	if (!value) {
		return null;
	}
	return startOfYear(parseRawDate(value));
}

export function getEndOfYear(value) {
	if (!value) {
		return null;
	}
	return endOfYear(parseRawDate(value));
}

export function isEmptyOrStartOfYear(value) {
	return !value || !compareDates(value, startOfYear(value));
}

export function isEmptyOrEndOfYear(value) {
	return !value || !compareDates(value, startOfDay(endOfYear(value)));
}

export function isInvalidDate(value) {
	return (value && value.getTime && isNaN(value.getTime()));
}

export function setRawDateToJSON() {
	Date.prototype.toJSON = function() {return format(this, 'yyyyMMdd');};
}

export function toRawDate(value) {
	if (!value) {
		return null;
	}
	return format(value, 'yyyyMMdd');
}

export function parseDate(value) {
	if (!value) {
		return null;
	}
	const tokens = value.trim().split(/[^0-9]+/);
	const tokenCount = tokens.length;
	if (tokenCount < 3) {
		return new Date(NaN);
	}
	let year = parseInt(tokens[yearIndex]);
	if (year < 100) {
		year += year <= currentYearMod100 ? 2000 : 1900;
	}
	const month = parseInt(tokens[monthIndex]) - 1;
	const day = parseInt(tokens[dayIndex]);
	let hour = 0, minute = 0, second = 0;
	if (tokenCount > 3) {
		hour = parseInt(tokens[3]);
		if (tokenCount > 4) {
			minute = parseInt(tokens[4]);
			if (tokenCount > 5) {
				second = parseInt(tokens[5]);
			}
		}
	}
	const res = new Date(year, month, day, hour, minute, second);
	if (!isInvalidDate(res)) {
		if (month !== res.getMonth() || day !== res.getDate() || hour !== res.getHours() || minute !== res.getMinutes()) {
			return new Date(NaN);
		}
	}
	return res;
}

export function formatRawDateTime(value) {
	if (typeof value === 'string') {
		value = parseRawDate(value);
	}
	if (value instanceof Date) {
		return format(value, 'yyyyMMddHHmmss');
	} else {
		return value;
	}
}

export function parseRawDate(value) {
	if (!value) {
		return null;
	}
	if (value instanceof Date) {
		return value;
	}
	const len = value.length;
	if (len === 8) {
		return parseISO(value);
	} else if (len > 8) {
		const date = value.substring(0, 8);
		let time = value.substring(8, 14);
		if (time.length < 6) {
			time += '000000'.substring(0, 6 - time.length);
		}
		return parseISO(date + 'T' + time);
	} else {
		return null;
	}
}

const today = startOfDay(new Date());
const minDate = new Date(1111, 1, 1);
const maxDate = new Date(3000, 1, 1);

export function getToday() {
	return startOfDay(new Date());
}

export function isActiveDateRange(startDate, endDate) {
	const now = new Date();
	const strt = parseRawDate(startDate) || minDate;
	const end = parseRawDate(endDate) || maxDate;
	return now.getTime() >= strt.getTime() && now.getTime() <= end.getTime();
}

export function compareDates(d1, d2) {
	if (!d1 || !d2) {
		return null;
	}
	const t1 = parseRawDate(d1).getTime();
	const t2 = parseRawDate(d2).getTime();
	return t1-t2;
}

export function compareDatesNullsFirst(d1, d2) {
	return compareDates(d1 || minDate, d2 || minDate);
}

export function compareDatesNullsLast(d1, d2) {
	return compareDates(d1 || maxDate, d2 || maxDate);
}

export function formatDate(value, includeTime) {
	if (!value) {
		return '';
	}
	let dateValue;
	if (typeof value === 'string') {
		dateValue = parseRawDate(value);
		if (value.length > 8) {
			if (includeTime === undefined) {
				includeTime = true;
			}
		} else {
			includeTime = false;
		}
	} else {
		dateValue = value;
	}
	if (dateValue >= MAX_DATE || dateValue <= MIN_DATE) {
		return '';
	}
	if (includeTime) {
		return format(dateValue, timeFormat);
	} else {
		return format(dateValue, dateFormat);
	}
}


export const EMPTY_DATE_RANGE = Immutable.Map({
	type: 'dateRange',
	valid: true,
	from: null,
	to: null,
	text: ''
});


const INVALID_DATE = Object.freeze(new Date(NaN));


function parseIncompleteDate(value, endDate) {
	if (typeof value !== 'string') {
		return null;
	}
	value = value.trim();
	if (!value) {
		return null;
	}
	if (value.toLowerCase() !== value.toUpperCase()) {
		return INVALID_DATE;
	}
	const tokens = value.split(/[\.,:]/g);
	let year, month, day;
	let eof = null;
	if (tokens.length === 1) {
		year = tokens[0];
		if (endDate) {
			month = 12;
			day = 31;
			eof = 'year';
		} else {
			month = 1;
			day = 1;
		}
	} else if (tokens.length === 2) {
		day = 1;
		if (dateOrder[0] === 'y') {
			year = tokens[0];
			month = tokens[1];
		} else {
			month = tokens[0];
			year = tokens[1];
		}
		if (endDate) {
			eof = 'month';
		}
	} else {
		if (dateOrder === 'dmy') {
			day = tokens[0];
			month = tokens[1];
			year = tokens[2];
		} else if (dateOrder === 'ymd') {
			year = tokens[0];
			month = tokens[1];
			day = tokens[2];
		}
		if (endDate) {
			eof = 'day';
		}
	}
	year = parseInt(year);
	month = parseInt(month);
	day = parseInt(day);
	if (isNaN(year) || isNaN(month) || isNaN(day)) {
		return INVALID_DATE;
	}
	if ((year>=10) && (year <= 99)) {
		if (year > currentYearMod100) {
			year += 1900;
		} else {
			year += 2000;
		}
	} else if (year < 1000 || year > 3000) {
		return INVALID_DATE;
	}
	let res = new Date(year, month - 1,	day);
	if (eof) {
		if (eof === 'day') {
			res = endOfDay(res);
		} else if (eof === 'month') {
			res = endOfMonth(res);
		} else if (eof === 'year') {
			res = endOfYear(res);
		}
	}
	return res;
}

export function createDateRange(from, to) {
	let text;
	if (from) {
		if (to) {
			text = formatDate(from, false) + ' - ' + formatDate(to, false);
		} else {
			text = formatDate(from, false) + ' - ';
		}
	} else {
		if (to) {
			text = ' - ' + formatDate(to);
		}
	}
	if (!text) {
		return EMPTY_DATE_RANGE;
	}
	return EMPTY_DATE_RANGE
		.set('from', from)
		.set('to', to)
		.set('text', text);
}

export function parseSearchDate(value) {
	if (typeof value !== 'string') {
		return EMPTY_DATE_RANGE;
	}
	value = value.trim();
	if (!value) {
		return EMPTY_DATE_RANGE;
	}
	const tokens = value.split('-');
	let from, to;
	if (tokens.length === 1) {
		from = parseIncompleteDate(tokens[0]);
		to = parseIncompleteDate(tokens[0], true);
	} else {
		from = parseIncompleteDate(tokens[0]);
		to = parseIncompleteDate(tokens[1], true);
	}
	const valid = !isInvalidDate(from) && !isInvalidDate(to);
	if (!valid) {
		from = null;
		to = null;
	}
	return Immutable.Map({
		type: 'dateRange',
		valid,
		fromTime: from ? from : null,
		toTime: to ? to : null,
		from: from ? format(from, 'yyyyMMdd') : null,
		to: to ? format(to, 'yyyyMMdd') : null,
		text: value
	});
}
